/**
 * Icons for the auth screens.
 *
 * The glyphs come from the shared icon library and draw with `currentColor`, so one copy
 * follows the light/dark theme. The three marks at the foot are drawn here instead: they
 * are brand artwork in fixed colours, not part of the icon set.
 */

import { ReactComponent as SvgCamera } from '../../../assets/images/icon/line/camera.svg';
import { ReactComponent as SvgChat } from '../../../assets/images/icon/line/chat-2.svg';
import { ReactComponent as SvgCheck } from '../../../assets/images/icon/line/check.svg';
import { ReactComponent as SvgChevronDown } from '../../../assets/images/icon/line/chevron-down.svg';
import { ReactComponent as SvgLock } from '../../../assets/images/icon/line/lock.svg';
import { ReactComponent as SvgMail } from '../../../assets/images/icon/line/mail.svg';
import { ReactComponent as SvgMoon } from '../../../assets/images/icon/line/moon.svg';
import { ReactComponent as SvgRefresh } from '../../../assets/images/icon/line/refresh-cw.svg';
import { ReactComponent as SvgSun } from '../../../assets/images/icon/line/sun.svg';
import { ReactComponent as SvgUserCircle } from '../../../assets/images/icon/solid/user-circle.svg';

export const SunIcon = ({ size = 17 }) => <SvgSun width={size} height={size} aria-hidden="true" />;

export const MoonIcon = ({ size = 17 }) => <SvgMoon width={size} height={size} aria-hidden="true" />;

export const ChevronDownIcon = () => <SvgChevronDown width="16" height="16" aria-hidden="true" />;

// `check` is what Auth.scss fades in and out with the selected state.
export const CheckIcon = () => <SvgCheck className="check" width="16" height="16" aria-hidden="true" />;

export const SmsIcon = () => <SvgChat width="16" height="16" aria-hidden="true" />;

export const MailIcon = () => <SvgMail width="18" height="18" aria-hidden="true" />;

export const LockIcon = () => <SvgLock width="16" height="16" aria-hidden="true" />;

export const CameraIcon = () => <SvgCamera width="15" height="15" aria-hidden="true" />;

export const RefreshIcon = () => <SvgRefresh width="24" height="24" aria-hidden="true" />;

// Sized by its container rather than by a prop, so it fills whichever avatar frame holds it.
export const AvatarPlaceholder = () => <SvgUserCircle aria-hidden="true" />;

export const DazwayMark = ({ size = 36 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        aria-hidden="true"
        // Inline so the global `svg { width: 100% }` rules in common.scss cannot
        // stretch the mark to the width of its column.
        style={{ width: size, height: size, flex: 'none' }}
    >
        <defs>
            <linearGradient id="dzAuthMark" x1="0" y1="0" x2="1" y2="1">
                <stop stopColor="#0060FC" />
                <stop offset="1" stopColor="#01194A" />
            </linearGradient>
        </defs>
        <path d="M26 12h20c21 0 38 17 38 38S67 88 46 88H26z" fill="url(#dzAuthMark)" />
        <path d="M34 42h26a6 6 0 0 1 6 6v22a6 6 0 0 1-6 6H50L34 88z" fill="#fff" />
        <rect x="34" y="54" width="20" height="34" rx="4" fill="url(#dzAuthMark)" />
    </svg>
);

export const GooglePlayIcon = () => (
    <svg width="18" height="20" viewBox="0 0 24 26" aria-hidden="true">
        <path d="M2 1.2 14.4 13 2 24.8A2 2 0 0 1 1 23V3a2 2 0 0 1 1-1.8Z" fill="#00C2FF" />
        <path d="M17.9 9.5 15.9 13 2 1.2 3.1.6 17.9 9.5Z" fill="#0FBC29" />
        <path d="M17.9 16.5 3.1 25.4 2 24.8 15.9 13l2 3.5Z" fill="#FF3A5E" />
        <path d="M22.6 11.3c1.3.8 1.3 2.6 0 3.4l-4.7 1.8-2-3.5 2-3.5 4.7 1.8Z" fill="#FFB23E" />
    </svg>
);

export const AppStoreIcon = () => (
    <svg width="17" height="20" viewBox="0 0 24 28" fill="#fff" aria-hidden="true">
        <path d="M17.5 14.6c0-3 2.4-4.4 2.5-4.5-1.4-2-3.5-2.3-4.2-2.3-1.8-.2-3.5 1-4.4 1s-2.3-1-3.8-1c-2 0-3.8 1.1-4.8 2.9-2 3.5-.5 8.7 1.5 11.6 1 1.4 2.1 3 3.6 2.9 1.5 0 2-.9 3.8-.9s2.3.9 3.8.9 2.6-1.4 3.5-2.8c1.1-1.6 1.6-3.2 1.6-3.3 0 0-3.1-1.2-3.1-4.5ZM14.6 5.5c.8-1 1.3-2.4 1.2-3.8-1.2 0-2.7.8-3.6 1.8-.7.9-1.4 2.3-1.2 3.7 1.4.1 2.8-.7 3.6-1.7Z" />
    </svg>
);
